import { REQUEST_BEGIN, REQUEST_FAIL, REQUEST_RESET_STATE, REQUEST_SUCCESS } from './actions'
import { FirstReturn, ServiceResponseError } from './typeHelpers'

export type Reducer<TState, TAction> = (prevState: TState, action: TAction) => TState
export type TypeOfOperation<Op> = Op extends (dispatch: any) => Promise<infer TData | null> ? TData : any

export type RawReducer<TApiOperation> = {
  [K in keyof TApiOperation]: Reducer<State<TypeOfOperation<FirstReturn<TApiOperation[K]>>>, Action<TypeOfOperation<FirstReturn<TApiOperation[K]>>>>
}

export type FirstReducerArg<T> = T extends (prevState: infer ARG, action: any) => any ? ARG : any
export type ApiState<TApiReducers> = {
  [K in keyof TApiReducers]: FirstReducerArg<TApiReducers[K]>
}

type InitialState = {
  loading: false,
  called: false,
  data: null,
  error: null
}

type LoadingState = {
  loading: true,
  called: true,
  data: null,
  error: null
}

type SuccessState<T> = {
  loading: false,
  called: true,
  data: T,
  error: null
}

type ErrorState = {
  loading: false,
  called: true,
  data: null,
  error: ServiceResponseError
}

export type State<T> = InitialState | LoadingState | ErrorState | SuccessState<T>

const INITIAL_STATE: InitialState = {
  loading: false,
  data: null,
  error: null,
  called: false
}

export type Action<T> = {
  type: string,
  payload: T | null | ServiceResponseError
}

export const createRequestReducer = <T> (requestName: string) => (prevState: State<T> = INITIAL_STATE, action: Action<T>): State<T> => {
  switch (action.type) {
    case `${REQUEST_BEGIN}/${requestName}`:
      return Object.assign({}, INITIAL_STATE, { loading: true, called: true })
    case `${REQUEST_SUCCESS}/${requestName}`:
      return Object.assign({}, INITIAL_STATE, { data: action.payload, called: true })
    case `${REQUEST_FAIL}/${requestName}`:
      return Object.assign({}, INITIAL_STATE, { error: action.payload, called: true })
    case `${REQUEST_RESET_STATE}/${requestName}`:
      return INITIAL_STATE
    default:
      return prevState
  }
}

export type ApiReducers<TRawApiOperations> = {
  [K in keyof TRawApiOperations]: Reducer<State<TypeOfOperation<
  FirstReturn<
  FirstReturn<TRawApiOperations[K]>
  >
  >>, any>
}
export const createApiReducers = <TRawApiOperations>(operationNames: (keyof TRawApiOperations)[]): ApiReducers<TRawApiOperations> => {
  const apiReducer = operationNames.reduce((acc: any, apiOperationName: any) => {
    acc[apiOperationName] = createRequestReducer(apiOperationName)
    return acc
  }, {})
  return apiReducer
}
